import { schema, t } from '@colyseus/schema'
import { PlatformSchema, ProjectileSchema, TrapSchema } from './entities'
import { PlayerSchema } from './PlayerSchema'
import { ResultSchema } from './ResultSchema'

/** Room phases (ARCHITECTURE §5.2). */
export const PHASES = ['waiting', 'countdown', 'racing', 'finished'] as const
export type RacePhase = (typeof PHASES)[number]

/**
 * Room state for `race` (ARCHITECTURE §4.2, PHASE1 T1.6), patched every 50 ms.
 *
 * One tick clock for countdown, racing and client clock sync: during `countdown` `tick` counts
 * up from `-countdownTicks` while the world stands still (runners at the spawn); GO is
 * `countdownEndTick` (0), and from then on `tick` is `World.tick`. Before the countdown `tick`
 * is 0 and the collections are empty except `players`.
 */
export const RaceState = schema(
  {
    phase: t.string<RacePhase>().default('waiting'),
    /** Room tick: negative during the countdown, `World.tick` while racing. */
    tick: t.number().default(0),
    /** Tick racing starts (GO, always 0); inputs with `t` below it are ignored. */
    countdownEndTick: t.number().default(0),
    seed: t.number().default(0),
    trackId: t.string().default(''),
    /** Runners keyed by session id (bots get a generated id of the same shape). */
    players: t.map(PlayerSchema),
    projectiles: t.array(ProjectileSchema),
    traps: t.array(TrapSchema),
    /** By crate index: true while the crate is taken (not yet respawned). */
    crateTaken: t.array('boolean'),
    /** Shards taken per runner (`World.coinsTaken`: `player * coins + coin`, D-056). */
    coinTaken: t.array('boolean'),
    /** By platform index (`World.platformState`). */
    platformState: t.array(PlatformSchema),
    /** Final standings in placement order, filled when the phase becomes `finished`. */
    results: t.array(ResultSchema),
    /** Private room join code, '' for public rooms. */
    code: t.string().default(''),
    /** Private room: the session allowed to send `"start"` and `"rematch"`. */
    hostSessionId: t.string().default(''),
  },
  'RaceState',
)
export type RaceState = InstanceType<typeof RaceState>
